
import React from "react"
import { useFormik } from "formik"
import { observer } from "mobx-react-lite"
import { useTheme } from "~/ui/styled"
import { Pressable, ScrollView } from "~/ui/primitives"
import { dismissKeyboardOnPress } from "~utils/keyboard"
import { SafeAreaView } from "~/ui/safe-area"
import { useSafeAreaInsets } from "~/ui/safe-area"
import { ColumnFlex, Text, Button } from "~components/El"
import { TextField } from "~components/InputField"
import { Header } from "~components/header"
import useNavigation from "~hooks/useNavigation"
import { useStores } from "~models"
import { useModal } from "~hooks/useModal"
import { Event, EventNames, UserUpdateData, FLAG_REPLY } from "~proto"
import { globalToast } from "~utils"
import { UpdateUserUserNameSchema } from "~schema"
import { scrollViewFillStyle } from "~/ui/scroll-layout"

export interface UpdateCurrentUserDisplayNameFormProps {
  onDismiss: () => void
}

const UpdateCurrentUserDisplayName: React.FC<UpdateCurrentUserDisplayNameFormProps> = ({
  onDismiss,
}) => {
  const theme = useTheme()
  const insets = useSafeAreaInsets()
  const navigation = useNavigation()
  const { eventStore, userStore } = useStores()
  const currentName = userStore.currentUser.username
  const { Dialog } = useModal()
  const dismiss = () => {
    if (onDismiss) {
      onDismiss()
    } else {
      navigation.goBack()
    }
  }

  const publishUpdate = (username: string) => {
    const evt = new Event<Partial<UserUpdateData>>(EventNames.USER_UPDATE, {
      username,
    })
    eventStore.publishAsync(evt, { flags: FLAG_REPLY }).then((res) => {
      if (res) {
        globalToast.show("账号修改成功")
      }
    }).catch((err) => {
      globalToast.show(err?.message ?? "账号修改失败")
    })
    dismiss()
  }

  const formik = useFormik({
    initialValues: { username: currentName },
    validationSchema: UpdateUserUserNameSchema,
    onSubmit: (values) => {
        Dialog.show({
          title: "提示",
          content: "保存本次编辑?",
          onConfirm: () => publishUpdate(values.username.trim()),
          onCancel: dismiss,
        })
    },
  })

  const { values, errors, touched, setFieldValue, setFieldTouched, handleSubmit, dirty, isValid } =
    formik

  const submitDisabled = !dirty || !isValid

  return (
    <SafeAreaView edges={["top", "bottom"]} style={{ flex: 1, minHeight: 0, overflow: "hidden", width: "100%", paddingTop: insets.top }}>
      <Pressable onClick={dismissKeyboardOnPress}>
        <Header
          headerText="修改账号"
          onLeftPress={dismiss}
          headerRight={
            <Button
              width="auto"
              height="30px"
              padding="0 12px"
              borderRadius="4px"
              bg={theme.colors.primary}
              onClick={() => handleSubmit()}
              disabled={submitDisabled}
            >
              <Text color={theme.colors.textLight} fontSize="14px">
                完成
              </Text>
            </Button>
          }
        />
      </Pressable>
      <ScrollView
        style={scrollViewFillStyle}
        keyboardShouldPersistTaps="never"
        keyboardDismissMode="on-drag"
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{
          paddingBottom: insets.bottom + 16,
        }}
      >
        <ColumnFlex width="100%" padding="12px" bg={theme.colors.blockBackground}>
          <TextField
            label="账号"
            value={values.username}
            placeholder="请输入账号"
            keyboardType="default"
            maxLength={30}
            onChangeText={(v) => setFieldValue("username", v)}
            onBlur={() => setFieldTouched("username", true)}
            error={touched.username && typeof errors.username === "string" ? errors.username : false}
          />
        </ColumnFlex>
      </ScrollView>
    </SafeAreaView>
  )
}

export default observer(UpdateCurrentUserDisplayName)
